import React, { useCallback, useMemo } from "react";
import { Typography } from "@mui/material";
import { useIntl } from "react-intl";
import CustomDialog from "./common/DialogContainer";
import ButtonComponent from "../components/common/ButtonComponent";
import { useDispatch, useSelector } from "react-redux";
import AppState from "store/AppState";
import {
  selectIsModalOpen,
  selectModalVariable,
} from "store/modal/ModalSelectors";
import { setModalState } from "store/modal/ModalActions";
import {
  selectProductColors,
  selectSelectedColorOption,
} from "store/product/ProductSelectors";
import { updateLeadingColorForMultipleProducts } from "store/color-management/ColorManagementActions";
import { getProductsColorMap } from "utils/ProductUtil";
import { selectCurrentCategory } from "store/category/CategorySelectors";

export const ConfirmationBulkLeadingColorActionModalId =
  "ConfirmationBulkLeadingColorActionModal";

const useStyles = () => {
  return {
    dialogAction: {
      width: "100%",
      textAlign: "end" as const,
      margin: "20px 20px 10px",
    },
    divMessage: {
      marginTop: "5px",
    },
  };
};

const ConfirmationBulkLeadingColorAction = () => {
  const dispatch = useDispatch();
  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, ConfirmationBulkLeadingColorActionModalId),
  );
  const selectedColorOption = useSelector((state: AppState) =>
    selectSelectedColorOption(state),
  );
  const intl = useIntl();
  const classes = useStyles();
  const productColorsData = useSelector(selectProductColors);
  const currentPageProductIds = useSelector((state: AppState) =>
    selectModalVariable(
      state,
      ConfirmationBulkLeadingColorActionModalId,
      "currentPageProductIds",
    ),
  );
  const currentPageProductsHavingLeadingColor = useSelector((state: AppState) =>
    selectModalVariable(
      state,
      ConfirmationBulkLeadingColorActionModalId,
      "currentPageProductsHavingLeadingColor",
    ),
  );
  const currentCategoryId = useSelector(selectCurrentCategory);
  const productsColorMap = useMemo(
    () => getProductsColorMap(productColorsData, currentPageProductIds),
    [productColorsData, currentPageProductIds],
  );
  const handleClose = useCallback(() => {
    dispatch(setModalState(ConfirmationBulkLeadingColorActionModalId, false));
  }, [dispatch]);

  const handleConfirmation = useCallback(() => {
    if (selectedColorOption && selectedColorOption !== "None") {
      // Get products and their colorIds for the selected color option
      const productsWithColorIds = Object.keys(productsColorMap)
        .map((productId) => {
          const productColors = productsColorMap[productId];
          const matchingColor = productColors?.find(
            (color) => color[selectedColorOption],
          );
          return matchingColor
            ? { productId, colorId: matchingColor[selectedColorOption] }
            : null;
        })
        ?.filter((product) => product !== null);

      dispatch(
        updateLeadingColorForMultipleProducts({
          products: productsWithColorIds,
          categoryId: currentCategoryId,
          productsColorsData: productColorsData,
        }),
      );
    } else if (selectedColorOption === "None") {
      dispatch(
        updateLeadingColorForMultipleProducts({
          products: currentPageProductsHavingLeadingColor,
          categoryId: currentCategoryId,
          productsColorsData: productColorsData,
        }),
      );
    }
    handleClose();
  }, [
    selectedColorOption,
    productsColorMap,
    currentPageProductsHavingLeadingColor,
    productColorsData,
    currentCategoryId,
    dispatch,
    handleClose,
  ]);

  const confirmationBulkLeadingColorActionTitle = () => {
    return (
      <Typography variant="inherit">
        {selectedColorOption === "None"
          ? intl.formatMessage({
              id: "colorManagementModal.modalTitle1",
              defaultMessage: "Unset Leading Color",
            })
          : intl.formatMessage({
              id: "bulkLeadingColorConfirmation.modalTitle2",
              defaultMessage: "Set Leading Color",
            })}
      </Typography>
    );
  };

  const confirmationBulkLeadingColorActionContent = () => {
    return (
      <div style={classes.divMessage}>
        <Typography variant="subtitle1">
          {selectedColorOption === "None" ? (
            <>
              {intl.formatMessage({
                id: "bulkLeadingColorConfirmation.instruction1a",
                defaultMessage:
                  "Are you sure you want to remove all Leading Color assignments for products on the current page?",
              })}
              <br />
              {intl.formatMessage({
                id: "bulkLeadingColorConfirmation.instruction1b",
                defaultMessage:
                  "Warning: This action will also remove any Leading Image assignments for the affected products.",
              })}
            </>
          ) : (
            <>
              {intl.formatMessage({
                id: "bulkLeadingColorConfirmation.instruction2",
                defaultMessage:
                  "Are you sure you want to set the Leading Color for all products that come in the selected color on the current page?",
              })}
              <br />
              {intl.formatMessage({
                id: "bulkLeadingColorConfirmation.instruction1b",
                defaultMessage:
                  "Warning: This action will also remove any Leading Image assignments for the affected products.",
              })}
            </>
          )}
        </Typography>
      </div>
    );
  };

  const dialogActions = () => {
    return (
      <div style={classes.dialogAction}>
        <ButtonComponent
          color="secondaryButtonColorCTABlue"
          variant="outlined"
          onClick={handleClose}
        >
          {intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
        </ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          onClick={() => {
            handleConfirmation();
          }}
        >
          {intl.formatMessage({
            id: "general.continue",
            defaultMessage: "Continue",
          })}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={() => handleClose()}
      title={confirmationBulkLeadingColorActionTitle()}
      actions={dialogActions()}
      maxWidth={"md"}
    >
      {confirmationBulkLeadingColorActionContent()}
    </CustomDialog>
  );
};

export default React.memo(ConfirmationBulkLeadingColorAction);
